/* eslint-disable react-hooks/exhaustive-deps */
import { useDispatch, useSelector } from "react-redux";
import { resourceUpdated, selectResourceById } from "../slices/resourceCollection/resourceCollectionSlice";
import { selectEditMode } from "../slices/editMode/editModeSlice";
import { changeTabMenu } from "../slices/tabPane/tabPaneSlice";
import { selectAllResourceChanges } from "../slices/resourceChangeReport/resourceChangeReportSlice";
import { memo, useEffect, useState } from "react";
import updateChangeReport from "../slices/resourceChangeReport/updateChangeReport";
import Spinner from "../components/Spinner";
import NarrativeEntry, { DisplayFromFHIRXhtml } from "../datatypeComponents/NarrativeEntry";
import CodeableConceptEntry, { DisplayFromFHIRCodeableConcept, displayStringFromFHIRCodeableConcept, simpleDisplayStringFromFHIRCodeableConcept } from "../datatypeComponents/CodeableConceptEntry";
import ReferenceEntry, { DisplayFromFHIRReference, displayStringFromFHIRReference } from "../datatypeComponents/ReferenceEntry";
import { DisplayFromFHIRAnnotation, displayStringFromFHIRAnnotation } from "../datatypeComponents/AnnotationEntry";
import { DateTimeEntry, getDateTimeDisplay } from "../datatypeComponents/PeriodEntry";
import BooleanEntry, { displayStringFromFHIRBoolean } from "../datatypeComponents/BooleanEntry";
import { changeTopNavigationElements } from "../slices/navigationPanel/navigationPanelSlice";
import StringEntry from "../datatypeComponents/StringEntry";
import CodeEntry from "../datatypeComponents/CodeEntry";
import ArrayEntry from "../datatypeComponents/ArrayEntry";
import updateElement, { updateNarrativeElement } from "../functions/updateElement";
import { orderedByValueSet } from "../dictionaries/THOandFHIRValueSets";
import { emptyReasonCodeableConceptSet, emptyReasonValueSet } from "../dictionaries/CodeSystemLookup";
import ExpandToAddOrEdit from "../components/ExpandToAddOrEdit";

const generateNarrativeForListResource = (resource) => {
    let status = "generated";
    let innerDiv = "";
    try {
        if (resource.title) {
            innerDiv += "<p><b>Title: </b>" + resource.title + "</p><br/>";
        }
        if (resource.status) {
            innerDiv += "<p><b>Status: </b>" + resource.status + "</p><br/>";
        }
        if (resource.date) {
            innerDiv += "<p><b>Date: </b>" + getDateTimeDisplay(resource.date) + "</p><br/>";
        }
        if (resource.mode) {
            innerDiv += "<p><b>Mode: </b>" + resource.mode + "</p><br/>";
        }
        if (resource.code) {
            innerDiv += "<p><b>Purpose: </b>" + displayStringFromFHIRCodeableConcept(resource.code) + "</p><br/>";
        }
        if (resource.subject?.length) {
            for (const subject of resource.subject) {
                if (subject) {
                    innerDiv += "<p><b>Subject: </b>" + displayStringFromFHIRReference(subject) + "</p><br/>";
                }
            }
        }
        if (resource.encounter) {
            innerDiv += "<p><b>Encounter: </b>" + displayStringFromFHIRReference(resource.encounter) + "</p><br/>";
        }
        if (resource.source) {
            innerDiv += "<p><b>Author/Creator: </b>" + displayStringFromFHIRReference(resource.source) + "</p><br/>";
        }
        if (resource.orderedBy) {
            innerDiv += "<p><b>Ordered by: </b>" + simpleDisplayStringFromFHIRCodeableConcept(resource.orderedBy) + "</p><br/>";
        }
        if (resource.note?.length) {
            for (const note of resource.note) {
                if (note) {
                    innerDiv += "<p><b>Note: </b>" + displayStringFromFHIRAnnotation(note) + "</p><br/>";
                }
            }
        }
        if (resource.entry) {
            innerDiv += "<table><tr><th>Flag</th><th>Date</th><th>Item</th></tr>";
            for (const entry of resource.entry) {
                let flag = simpleDisplayStringFromFHIRCodeableConcept(entry.flag) || "";
                let itemDeleted = entry.deleted ? "DELETED" : "";
                let date = getDateTimeDisplay(entry.date) || "";
                let item = displayStringFromFHIRReference(entry.item) || "";

                innerDiv += "<tr><td>" + flag + " " + itemDeleted + "</td><td>" +
                    date + "</td><td>" + item + "</td></tr>";
            }
            innerDiv += "</table><br/>";
        }
        if (resource.emptyReason) {
            innerDiv += "<p><b>Why empty: </b>" + simpleDisplayStringFromFHIRCodeableConcept(resource.emptyReason) + "</p><br/>";
        }
    } catch {
        innerDiv = "[Unable to generate Narrative Summary.]"
    }
    return { "status": status, "div": '<div xmlns="http://www.w3.org/1999/xhtml">' + innerDiv + "</div>" };
}

const ListEntryEntry = memo(({ elementName, fieldLabel, startingValue, setResourceState, startCollapsed,
    referencedResourceTypes, startingResourceType, setProfile, selectProfile }) => {
    let startingListEntry = {
        flag: "", deleted: "", date: "", item: ""
    };
    if (!startingValue) {
        startingValue = "";
    } else {
        if (startingValue.extension) { startingListEntry.extension = startingValue.extension; }
        if (startingValue.flag) { startingListEntry.flag = startingValue.flag; }
        if (typeof startingValue.deleted === "boolean") { startingListEntry.deleted = startingValue.deleted; }
        if (startingValue.date) { startingListEntry.date = startingValue.date; }
        if (startingValue.item) { startingListEntry.item = startingValue.item; }
    }

    const [listEntryState, setListEntryState] = useState(JSON.parse(JSON.stringify(startingListEntry || {})));
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

    useEffect((() => {
        if (Object.keys(listEntryState).length > 0) {
            let newListEntry = {};
            if (listEntryState.extension) { newListEntry.extension = listEntryState.extension; }
            if (listEntryState.flag && Object.keys(listEntryState.flag).length > 0) {
                newListEntry.flag = listEntryState.flag;
            }
            if (typeof listEntryState.deleted === "boolean") { newListEntry.deleted = listEntryState.deleted; }
            if (listEntryState.date) { newListEntry.date = listEntryState.date; }
            if (listEntryState.item && Object.keys(listEntryState.item).length > 0) {
                newListEntry.item = listEntryState.item;
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newListEntry } });
        }
    }), [listEntryState]);

    if (startCollapsedState) {
        return <div>
            <p><b>{fieldLabel}: </b></p>
            {startingValue && <div style={{ marginLeft: "24px" }}>
                {startingValue.flag &&
                    <div>
                        <b>Flag: </b><DisplayFromFHIRCodeableConcept codeableConcept={startingValue.flag} />
                        <br /><br />
                    </div>}
                {startingValue.item &&
                    <div><DisplayFromFHIRReference reference={startingValue.item} /><br /></div>}
                {(typeof startingValue.deleted === "boolean") &&
                    <p><b>Deleted: </b>{displayStringFromFHIRBoolean(startingValue.deleted)}</p>}
                {startingValue.date &&
                    <p><b>Date added: </b>{getDateTimeDisplay(startingValue.date)}</p>}
            </div>}
            &nbsp;&nbsp;
            <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
        </div>
    } else {
        return <>
            <p style={{ marginBottom: "0px" }}><b>{fieldLabel}: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <CodeableConceptEntry elementName='flag' fieldLabel='Flag'
                    startingValue={listEntryState.flag} startCollapsed
                    setResourceState={setListEntryState} />
                <BooleanEntry elementName='deleted' fieldLabel='Deleted'
                    startingValue={listEntryState.deleted} setResourceState={setListEntryState} />
                <DateTimeEntry elementName='date' fieldLabel='Date added'
                    startingValue={listEntryState.date} setResourceState={setListEntryState} />
                <ReferenceEntry elementName='item' fieldLabel='The Item'
                    startingValue={listEntryState.item} enableCreation={true} startCollapsed
                    referencedResourceTypes={referencedResourceTypes}
                    startingResourceType={startingResourceType}
                    setProfile={setProfile}
                    selectProfile={selectProfile}
                    setResourceState={setListEntryState} />
            </div>
        </>
    }
})

const ListDisplay = ({ foi }) => {
    const dispatch = useDispatch();
    const editMode = useSelector(state => selectEditMode(state));
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    const resourceChanges = useSelector(state => selectAllResourceChanges(state));
    const resourceJson = parentResourceDetail?.revisedResourceJson;

    const [listResourceState, setListResourceState] = useState({});
    const [listReferencesState, setListReferencesState] = useState({ "references": [], "loaded": false });

    useEffect(() => {
        if (resourceJson) {
            setListResourceState(resourceJson);
            dispatch(changeTopNavigationElements(["Summary", "Description", "List Entries"]));
            dispatch(changeTabMenu(["Text View", "JSON View", "Usage View"]));
            if (!listReferencesState?.loaded) {
                let listReferences = [];
                if (resourceJson?.entry) {
                    listReferences = resourceJson.entry.map(entry => { return entry.item }) || [];
                }
                let setProfile;
                let selectProfile;
                let referencedResourceTypes;
                let startingResourceType;
                if (resourceJson.code) {
                    let listTypeCode = resourceJson.code.coding?.[0]?.code || resourceJson.code.text;
                    if (listTypeCode === "BaselineVariablesList" || listTypeCode === "Baseline Variables List") {
                        setProfile = "VariableDefinition";
                        startingResourceType = "EvidenceVariable";
                        referencedResourceTypes = ["EvidenceVariable"];
                    }
                    if (listTypeCode === "EvidenceList" || listTypeCode === "Evidence List") {
                        startingResourceType = "Evidence";
                        referencedResourceTypes = ["Evidence"];
                    }
                    if (listTypeCode === "NetEffectContributionList" || listTypeCode === "Net Effect Contributions List") {
                        setProfile = "NetEffectContribution";
                        startingResourceType = "Evidence";
                        referencedResourceTypes = ["Evidence"];
                    }
                    if (listTypeCode === "OutcomeList" || listTypeCode === "Outcome List") {
                        referencedResourceTypes = ["Group", "EvidenceVariable"];
                    }
                    if (listTypeCode === "OutcomeVariablesList" || listTypeCode === "Outcome Variables List") {
                        setProfile = "VariableDefinition";
                        startingResourceType = "EvidenceVariable";
                        referencedResourceTypes = ["EvidenceVariable"];
                    }
                    if (listTypeCode === "ParticipantFlowVariablesList" || listTypeCode === "Participant Flow Variables List") {
                        selectProfile = ["ParticipantFlowEvidenceVariable", "ParticipantFlowReasonEvidenceVariable"];
                        startingResourceType = "EvidenceVariable";
                        referencedResourceTypes = ["EvidenceVariable"];
                    }
                    if (listTypeCode === "VariablesList" || listTypeCode === "Variables List") {
                        startingResourceType = "EvidenceVariable";
                        referencedResourceTypes = ["EvidenceVariable"];
                        selectProfile = ["VariableDefinition", "ParticipantFlowEvidenceVariable", "ParticipantFlowReasonEvidenceVariable"];
                    }
                }
                setListReferencesState({
                    "references": listReferences, "loaded": true,
                    "setProfile": setProfile, "selectProfile": selectProfile,
                    "startingResourceType": startingResourceType,
                    "referencedResourceTypes": referencedResourceTypes
                });
            }
        }
    }, [resourceJson]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateNarrativeElement(listResourceState, "text", "Narrative", parentResourceDetail,
                resourceChanges, dispatch, resourceUpdated, updateChangeReport);
        }
    }, [listResourceState.text]);

    useEffect(() => {
        if (editMode && resourceJson) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = newResourceDetail.resourceJson.title;
            previousFinalValue = newResourceDetail.revisedResourceJson.title;
            newResourceDetail.revisedResourceJson.title = listResourceState.title;
            newResourceDetail.displayTitle = listResourceState.title || newResourceDetail.displayTitle;
            if (listResourceState.title !== undefined &&
                previousFinalValue !== listResourceState.title) {
                console.log("resourceUpdate for List.title");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "string",
                    "title",
                    previousInitialValue,
                    listResourceState.title);
            }
        }
    }, [listResourceState.title]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(listResourceState, "status", "code", parentResourceDetail,
                resourceChanges, dispatch, resourceUpdated, updateChangeReport);
        }
    }, [listResourceState.status]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(listResourceState, "mode", "code", parentResourceDetail,
                resourceChanges, dispatch, resourceUpdated, updateChangeReport);
        }
    }, [listResourceState.mode]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(listResourceState, "code", "CodeableConcept", parentResourceDetail,
                resourceChanges, dispatch, resourceUpdated, updateChangeReport);
        }
    }, [listResourceState.code]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(listResourceState, "subject", "ArrayOfReference", parentResourceDetail,
                resourceChanges, dispatch, resourceUpdated, updateChangeReport);
        }
    }, [listResourceState.subject]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(listResourceState, "encounter", "Reference", parentResourceDetail,
                resourceChanges, dispatch, resourceUpdated, updateChangeReport);
        }
    }, [listResourceState.encounter]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(listResourceState, "date", "dateTime", parentResourceDetail,
                resourceChanges, dispatch, resourceUpdated, updateChangeReport);
        }
    }, [listResourceState.date]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(listResourceState, "source", "Reference", parentResourceDetail,
                resourceChanges, dispatch, resourceUpdated, updateChangeReport);
        }
    }, [listResourceState.source]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(listResourceState, "orderedBy", "CodeableConcept", parentResourceDetail,
                resourceChanges, dispatch, resourceUpdated, updateChangeReport);
        }
    }, [listResourceState.orderedBy]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(listResourceState, "note", "ArrayOfAnnotation", parentResourceDetail,
                resourceChanges, dispatch, resourceUpdated, updateChangeReport);
        }
    }, [listResourceState.note]);

    useEffect(() => {
        if (listReferencesState?.references?.length) {
            let listEntries = listReferencesState.references.map(entry => { return { "item": entry } });
            setListResourceState(prevState => { return { ...prevState, entry: listEntries } });
        }
    }, [listReferencesState]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(listResourceState, "entry", "ArrayOfListEntry", parentResourceDetail,
                resourceChanges, dispatch, resourceUpdated, updateChangeReport);
        }
    }, [listResourceState.entry]);

    if (!resourceJson) {
        return <Spinner />;
    }

    return <div>
        <div style={{ marginTop: "12px" }}>
            <h3 id="summary">Summary</h3>
            <div style={{ marginLeft: "24px" }}>
                {editMode ?
                    <NarrativeEntry elementName="text"
                        startingValue={listResourceState.text}
                        generatedNarrative={generateNarrativeForListResource(listResourceState)}
                        setResourceState={setListResourceState} />
                    :
                    <>{
                        (resourceJson?.text?.div &&
                            resourceJson.text.status &&
                            resourceJson.text.status !== "empty")
                            ?
                            <DisplayFromFHIRXhtml xhtml={resourceJson.text.div} />
                            :
                            <p>[Narrative summary not generated.]</p>
                    }</>
                }
            </div>
            <h3 id="description">Description</h3>
            <div style={{ marginLeft: "24px" }}>
                {editMode ?
                    <StringEntry elementName="title" fieldLabel="Title"
                        startingValue={listResourceState.title}
                        setResourceState={setListResourceState} />
                    :
                    <>{resourceJson.title && <p><b>Title: </b>{resourceJson.title}</p>}</>
                }
                {editMode ?
                    <CodeEntry elementName='status' fieldLabel='Status'
                        allowedValues={['current', 'retired', 'entered-in-error']}
                        startingValue={listResourceState.status}
                        setResourceState={setListResourceState} />
                    :
                    <>{resourceJson.status && <p><b>Status: </b>{resourceJson.status}</p>}</>
                }
                {editMode ?
                    <CodeEntry elementName='mode' fieldLabel='Mode'
                        allowedValues={['working', 'snapshot', 'changes']}
                        startingValue={listResourceState.mode}
                        setResourceState={setListResourceState} />
                    :
                    <>{resourceJson.mode && <p><b>Mode: </b>{resourceJson.mode}</p>}</>
                }
                {editMode ?
                    <CodeableConceptEntry elementName='code' fieldLabel='Purpose'
                        startCollapsed startingValue={listResourceState.code}
                        setResourceState={setListResourceState} />
                    :
                    <>{resourceJson.code && <div>
                        <b>Purpose: </b><DisplayFromFHIRCodeableConcept codeableConcept={resourceJson.code} />
                        <br /><br />
                    </div>}</>
                }

                {editMode ?
                    <ArrayEntry datatype='Reference' elementName='subject' fieldLabel='Subject'
                        enableCreation={true} startCollapsed
                        startingValue={listResourceState.subject}
                        setResourceState={setListResourceState} />
                    :
                    <>
                        {(resourceJson.subject?.length === 1) &&
                            <p><b>Subject: </b><DisplayFromFHIRReference reference={resourceJson.subject[0]} /></p>}
                        {(resourceJson.subject?.length > 1) && resourceJson.subject.map((subject, subjectIndex) => {
                            return <div key={subjectIndex}>
                                <div><b>Subject: </b><DisplayFromFHIRReference reference={subject} /></div>
                            </div>
                        })}
                    </>
                }
                {editMode ?
                    <ReferenceEntry elementName='encounter' fieldLabel='Encounter'
                        enableCreation={true} referencedResourceTypes={['Encounter']} startCollapsed
                        startingValue={listResourceState.encounter}
                        setResourceState={setListResourceState} />
                    :
                    <>{resourceJson.encounter &&
                        <p><b>Encounter: </b><DisplayFromFHIRReference reference={resourceJson.encounter} /></p>}</>
                }
                {editMode ?
                    <DateTimeEntry elementName='date' fieldLabel='Date'
                        startingValue={listResourceState.date}
                        setResourceState={setListResourceState} />
                    :
                    <>{resourceJson.date && <p><b>Date: </b>{getDateTimeDisplay(resourceJson.date)}</p>}</>
                }
                {editMode ?
                    <ReferenceEntry elementName='source' fieldLabel='Source (Author)'
                        enableCreation={true} startCollapsed
                        referencedResourceTypes={['Practitioner', 'PractitionerRole', 'Patient', 'Device', 'Organization', 'RelatedPerson', 'CareTeam']}
                        startingValue={listResourceState.source}
                        setResourceState={setListResourceState} />
                    :
                    <>
                        {resourceJson.source &&
                            <p><b>Source (Author): </b><DisplayFromFHIRReference reference={resourceJson.source} /></p>}
                    </>
                }
                {editMode ?
                    <CodeableConceptEntry elementName='orderedBy'
                        fieldLabel='Ordered by' startingValue={listResourceState.orderedBy || null}
                        valueSet={orderedByValueSet} startCollapsed
                        setResourceState={setListResourceState} />
                    :
                    <>
                        {resourceJson.orderedBy && <div>
                            <b>Ordered by: </b><DisplayFromFHIRCodeableConcept codeableConcept={resourceJson.orderedBy} />
                            <br /><br />
                        </div>}
                    </>
                }
                {editMode ?
                    <ArrayEntry datatype='Annotation' elementName='note' fieldLabel='Note'
                        startingValue={listResourceState.note || null} startCollapsed
                        setResourceState={setListResourceState} />
                    :
                    <>
                        {(resourceJson.note?.length === 1) &&
                            <p><b>Note: </b><DisplayFromFHIRAnnotation annotation={resourceJson.note[0]} /></p>}
                        {(resourceJson.note?.length > 1) && resourceJson.note.map((note, noteIndex) => {
                            return <div key={noteIndex}>
                                <div><b>Note: </b><DisplayFromFHIRAnnotation annotation={note} /></div>
                            </div>
                        })}
                    </>
                }
            </div>
            <h3 id="list-entries">List Entries</h3>
            <div style={{ marginLeft: "24px" }}>
                {editMode ?
                    <>
                        {listReferencesState?.loaded && <>
                            <p>To manage the list entries as a simple set of resource references:</p>
                            <div style={{ marginLeft: "24px" }}>
                                <ArrayEntry datatype='Reference' elementName='references'
                                    fieldLabel={(listReferencesState.setProfile ||
                                        listReferencesState.startingResourceType ||
                                        'The') + ' Resources'}
                                    startingValue={listReferencesState.references || []}
                                    referencedResourceTypes={listReferencesState.referencedResourceTypes}
                                    startingResourceType={listReferencesState.startingResourceType}
                                    setProfile={listReferencesState.setProfile}
                                    selectProfile={listReferencesState.selectProfile}
                                    startCollapsed startEmptyArrayClosed enableCreation={true}
                                    setResourceState={setListReferencesState} />
                            </div>
                        </>}
                        <ArrayEntry datatype='ListEntry' elementName='entry' startCollapsed
                            fieldLabel='Entry' startingValue={listResourceState.entry}
                            referencedResourceTypes={listReferencesState.referencedResourceTypes}
                            startingResourceType={listReferencesState.startingResourceType}
                            setProfile={listReferencesState.setProfile}
                            selectProfile={listReferencesState.selectProfile}
                            setResourceState={setListResourceState} />
                    </>
                    :
                    <>
                        {Array.isArray(resourceJson.entry) && resourceJson.entry.map((entry, entryIndex) => {
                            return <div key={entryIndex}>
                                <p><b>Entry {entryIndex + 1}: </b></p>
                                <div style={{ marginLeft: "24px" }}>
                                    {entry.flag && <div><b>Flag: </b><DisplayFromFHIRCodeableConcept codeableConcept={entry.flag} />
                                        <br /><br /></div>}
                                    {entry.item && <p><DisplayFromFHIRReference reference={entry.item} /></p>}
                                    {(typeof entry.deleted === "boolean") && <p><b>Deleted: </b>{displayStringFromFHIRBoolean(entry.deleted)}</p>}
                                    {entry.date && <p><b>Date added: </b>{getDateTimeDisplay(entry.date)}</p>}
                                </div>
                            </div>
                        })}
                    </>
                }
                {editMode ?
                    <>{
                        (!listResourceState.entry || listResourceState.entry.length === 0) && <>
                            <br />
                            <p><b>Explain why this List is empty</b></p>
                            <CodeableConceptEntry elementName='emptyReason'
                                fieldLabel='Empty Reason' startingValue={listResourceState.emptyReason || null}
                                valueSet={emptyReasonValueSet} startCollapsed
                                codeableConceptLevelValueSet={emptyReasonCodeableConceptSet}
                                setResourceState={setListResourceState} />
                        </>
                    }</>
                    :
                    <>{
                        ((!resourceJson.entry || resourceJson.entry.length === 0) &&
                            resourceJson.emptyReason) &&
                        <span><b>Why Empty: </b><DisplayFromFHIRCodeableConcept
                            codeableConcept={resourceJson.emptyReason} /></span>
                    }</>
                }
            </div>
        </div>
    </div>
}

export default ListDisplay;
export { ListEntryEntry };